import {
  RECOVERY_DEMOTION_EVIDENCE_WINDOW_MS,
  decideRecoveryDemotion,
  type LinkTroubleSample,
  type RecoveryDemotionDecision,
} from '@playcastdotio/PeerCore';

/**
 * How long a peer's samples survive after its connection is gone. The transport-trouble
 * handler and the recovery re-offer both fire after teardown, so the record has to outlive
 * the connection it describes; it does not need to outlive the reclaim it informs.
 */
export const LINK_TROUBLE_RETENTION_MS = RECOVERY_DEMOTION_EVIDENCE_WINDOW_MS * 2;

/**
 * LinkTroubleLedger - the recent link record the recovery demotion reads.
 *
 * One sample per host stats poll per peer (loss, RTT, encoder limitation, recovery floor,
 * standing verdict). Nothing here decides anything; `decideRecoveryDemotion` does, and this
 * ledger only makes sure the samples it needs are still there when the peer is already gone.
 * Samples older than the retention are pruned on every write and read.
 */
export class LinkTroubleLedger {
  private readonly samplesByPeer = new Map<string, LinkTroubleSample[]>();
  private readonly nowMs: () => number;

  constructor(nowMs: () => number = () => Date.now()) {
    this.nowMs = nowMs;
  }

  public record(peerId: string, sample: Omit<LinkTroubleSample, 'atMs'> & { atMs?: number }): void {
    const now = this.nowMs();
    const list = this.samplesByPeer.get(peerId) ?? [];
    list.push({ ...sample, atMs: sample.atMs ?? now });
    this.samplesByPeer.set(peerId, this.prune(list, now));
    this.sweep(now);
  }

  /** The demotion decision for a peer as of now, from whatever is still on record. */
  public decide(peerId: string): RecoveryDemotionDecision {
    const now = this.nowMs();
    const list = this.prune(this.samplesByPeer.get(peerId) ?? [], now);
    return decideRecoveryDemotion(list, now);
  }

  /** Drop a peer's record outright (a reconnect decided against it; nothing further will read it). */
  public forget(peerId: string): void {
    this.samplesByPeer.delete(peerId);
  }

  private prune(list: LinkTroubleSample[], now: number): LinkTroubleSample[] {
    const since = now - LINK_TROUBLE_RETENTION_MS;
    return list.filter((s) => s.atMs >= since);
  }

  private sweep(now: number): void {
    for (const [peerId, list] of this.samplesByPeer) {
      if (list.length === 0 || list[list.length - 1].atMs < now - LINK_TROUBLE_RETENTION_MS) this.samplesByPeer.delete(peerId);
    }
  }
}
